/* eslint-disable */
import React, { useState, useEffect } from "react";
import {
  Modal,
  Divider,
  TextInput,
  Button,
  Box,
  Select,
  Group,
  Text,
  Loader,
} from "@mantine/core";

import ModalHeader from "components/ModalHeader";
import { useChangeLeaveType } from "api/newLeave/adminLeaveType";
import { useGetLeaveBalance } from "api/newLeave/getLeavebalance";

type LeaveTypeModalProps = {
  opened: boolean;
  isApproved?: boolean;
  onClose: () => void;
  leaveId: string;
};

const leaveTypeOptions = [
  { value: "annual", label: "Annual Leave" },
  { value: "sick", label: "Sick Leave" },
  { value: "pressingNecessity", label: "Pressing Necessity" },
  { value: "compassionate", label: "Compassionate Leave" },
  { value: "bereavement", label: "Bereavement Leave" },
  { value: "juryDuty", label: "Jury Duty" },
  { value: "election", label: "Election Leave" },
  { value: "bandBusiness", label: "Band Business Leave" },
  { value: "governance", label: "Governance Leave" },
  { value: "education", label: "Education Leave" },
  { value: "professionalDevelopment", label: "Professional Development" },
  { value: "culturalSpiritual", label: "Cultural & Spiritual Leave" },
  { value: "leaveWithoutPay", label: "Leave Without Pay (LWP)" },
  { value: "other", label: "Other" },
];

const apiLeaveTypeMap: Record<string, string> = {
  annual: "annualLeave",
  sick: "sickLeave",
  pressingNecessity: "pressingNecessity",
  compassionate: "compassionateLeave",
  bereavement: "bereavementLeave",
  juryDuty: "juryDuty",
  election: "election",
  bandBusiness: "bandBusiness",
  governance: "governance",
  education: "education",
  professionalDevelopment: "professionalDevelopment",
  culturalSpiritual: "culturalSpiritual",
  leaveWithoutPay: "leaveWithoutPay",
  other: "other",
};

function LeaveTypeModal({ opened, onClose, leaveId }: LeaveTypeModalProps) {
  const { mutate: changeLeaveType, isLoading: changingType } =
    useChangeLeaveType();
  const { data: leaveBalanceData, isLoading: balanceLoading } =
    useGetLeaveBalance();

  const [newLeaveType, setNewLeaveType] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [typeError, setTypeError] = useState<string | null>(null);
  const [balance, setBalance] = useState<number | null>(null);

  useEffect(() => {
    if (!newLeaveType) {
      setBalance(null);
      return;
    }

    const api = (leaveBalanceData as any)?.data;
    if (api) {
      const map: Record<string, number | null> = {
        annual: api.annualLeave?.balance ?? api.annualLeave?.allocated ?? null,
        sick: api.sickLeave?.balance ?? api.sickLeave?.accrued ?? null,
        pressingNecessity: api.pressingNecessity?.balance ?? null,
        compassionate: api.compassionate?.balance ?? null,
        bereavement: api.bereavement?.balance ?? null,
        juryDuty: api.juryDuty?.balance ?? null,
        election: api.election?.balance ?? null,
        bandBusiness: api.bandBusiness?.balance ?? null,
        governance: api.governance?.balance ?? null,
        education: api.education?.balance ?? null,
        professionalDevelopment: api.professionalDevelopment?.balance ?? null,
        culturalSpiritual: api.culturalSpiritual?.balance ?? null,
        leaveWithoutPay: null,
        other: null,
      };

      setBalance(map[newLeaveType] ?? null);
    } else {
      setBalance(null);
    }
  }, [newLeaveType, leaveBalanceData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!newLeaveType) {
      setTypeError("Please select a new leave type");
      return;
    }

    setError(null);
    setTypeError(null);

    changeLeaveType({
      leaveId,
      newLeaveType,
    });

    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="md"
      withCloseButton={false}
      transitionDuration={300}
      centered
      overlayOpacity={0.5}
    >
      <ModalHeader
        title={"Leave Type Modal"}
        onClose={onClose}
        ariaLabel="Close Status modal"
      />
      <Divider mb="md" />

      <Box
        component="form"
        onSubmit={handleSubmit}
        style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
      >
        <Select
          label="Change Leave Type"
          placeholder="Select new leave type"
          data={leaveTypeOptions}
          value={newLeaveType}
          onChange={(value) => {
            setNewLeaveType(value);
            if (typeError) setTypeError(null);
          }}
          error={typeError}
        />

        <Box
          sx={(theme) => ({
            border: "1px solid #e9ecef",
            borderRadius: 6,
            padding: theme.spacing.md,
          })}
        >
          <Group position="apart" align="center">
            <Text weight={600}>Current Balance</Text>
            {balanceLoading ? (
              <Loader size={18} />
            ) : (
              <Text>{balance != null ? `${balance} days` : "0"}</Text>
            )}
          </Group>
        </Box>

        <Group mt="md">
          <Button type="submit" fullWidth loading={changingType}>
            Submit
          </Button>
        </Group>
      </Box>
    </Modal>
  );
}

export default LeaveTypeModal;
